/**
 * Rich-text editor for a creative variant.
 *
 * Surface contracts:
 * - value is markdown-flavoured plain text. **bold**, __underline__, links
 *   as [text](url), custom emoji as :name: where :name: resolves to a MediaItem
 *   (kind=emoji). Telegram preview re-renders these inline.
 * - onChange(string) on every edit.
 * - mediaList is the variant's media array (id ordering matters for the post
 *   image carousel; drag-reorder updates parent state).
 */
import { useRef, useState, type DragEvent } from "react";
import {
  Bold, Italic, Link as LinkIcon, Smile, Underline, Trash2, GripVertical,
} from "lucide-react";
import { useMyMedia, useUploadMedia, type MediaItem } from "@/shared/api/wb";
import { cn } from "@/shared/lib/cn";

const PRESET_EMOJI = ["🔥", "💡", "✨", "🚀", "👌", "❤️", "🎁", "👀", "💸", "😅"];

export type VariantMedia = { id: string; kind: "image" | "video"; url: string };

export function CreativeEditor({
  text, onText, media, onMedia,
}: {
  text: string; onText: (s: string) => void;
  media: VariantMedia[]; onMedia: (m: VariantMedia[]) => void;
}) {
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const upload = useUploadMedia();
  const myEmoji = useMyMedia("emoji").data ?? [];
  const [emojiOpen, setEmojiOpen] = useState(false);

  // Always-current refs so async onFile sees latest values across re-renders
  const mediaRef = useRef(media);
  mediaRef.current = media;
  const onMediaRef = useRef(onMedia);
  onMediaRef.current = onMedia;

  function wrap(pre: string, post: string = pre) {
    const ta = taRef.current;
    if (!ta) return;
    const [s, e] = [ta.selectionStart, ta.selectionEnd];
    const sel = text.slice(s, e) || "текст";
    const next = text.slice(0, s) + pre + sel + post + text.slice(e);
    onText(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = s + pre.length;
      ta.selectionEnd = s + pre.length + sel.length;
    });
  }

  function insertAtCursor(snippet: string) {
    const ta = taRef.current;
    if (!ta) return onText(text + snippet);
    const [s, e] = [ta.selectionStart, ta.selectionEnd];
    onText(text.slice(0, s) + snippet + text.slice(e));
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = s + snippet.length;
    });
  }

  function onLink() {
    const url = prompt("Адрес ссылки:");
    if (!url) return;
    const ta = taRef.current;
    if (!ta) return;
    const [s, e] = [ta.selectionStart, ta.selectionEnd];
    const sel = text.slice(s, e) || "ссылка";
    const next = `${text.slice(0, s)}[${sel}](${url})${text.slice(e)}`;
    onText(next);
  }

  async function onFile(files: FileList | null) {
    if (!files) return;
    const acc: VariantMedia[] = [...mediaRef.current];
    for (const f of Array.from(files)) {
      const isVideo = f.type.startsWith("video/");
      try {
        const out = await upload.mutateAsync({ file: f, kind: isVideo ? "video" : "image" });
        acc.push({ id: out.id, kind: out.kind as "image" | "video", url: out.url });
        onMediaRef.current([...acc]);
      } catch (e) {
        alert(`Не удалось загрузить ${f.name}: ${e instanceof Error ? e.message : "ошибка"}`);
      }
    }
  }

  async function onEmojiUpload(file: File) {
    const label = prompt("Имя эмодзи (без пробелов, латиница):", "emoji");
    if (!label) return;
    try {
      await upload.mutateAsync({ file, kind: "emoji", label });
    } catch (e) {
      alert(`Не удалось загрузить эмодзи: ${e instanceof Error ? e.message : "ошибка"}`);
    }
  }

  // Drag-and-drop reorder
  const [dragId, setDragId] = useState<string | null>(null);
  function onDragStart(e: DragEvent<HTMLDivElement>, id: string) {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
  }
  function onDragOver(e: DragEvent<HTMLDivElement>) { e.preventDefault(); }
  function onDrop(e: DragEvent<HTMLDivElement>, targetId: string) {
    e.preventDefault();
    if (!dragId || dragId === targetId) return;
    const src = media.findIndex((m) => m.id === dragId);
    const dst = media.findIndex((m) => m.id === targetId);
    if (src < 0 || dst < 0) return;
    const next = [...media];
    const [moved] = next.splice(src, 1);
    next.splice(dst, 0, moved);
    onMedia(next);
    setDragId(null);
  }

  function removeMedia(id: string) {
    onMedia(media.filter((m) => m.id !== id));
  }

  return (
    <div className="grid gap-3">
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
        multiple
        hidden
        onChange={(e) => { onFile(e.target.files); e.target.value = ""; }}
      />

      <Toolbar
        onBold={() => wrap("**")}
        onItalic={() => wrap("__")}
        onUnderline={() => wrap("~")}
        onLink={onLink}
        onEmojiClick={() => setEmojiOpen((o) => !o)}
        emojiOpen={emojiOpen}
      />

      {emojiOpen && (
        <div className="rounded-md border border-ink-12 bg-white p-3 grid gap-2 fade-up">
          <div className="text-[11px] font-display font-semibold uppercase tracking-[0.07em] text-ink-40 mb-1">Стандартные</div>
          <div className="flex gap-1 flex-wrap">
            {PRESET_EMOJI.map((em) => (
              <button key={em} onClick={() => insertAtCursor(em)}
                className="w-9 h-9 grid place-items-center text-[18px] rounded-sm hover:bg-cream">
                {em}
              </button>
            ))}
          </div>
          <div className="text-[11px] font-display font-semibold uppercase tracking-[0.07em] text-ink-40 mt-2 mb-1">Кастомные</div>
          <div className="flex gap-1 flex-wrap">
            {myEmoji.map((e) => (
              <button key={e.id} title={`:${e.label}:`}
                onClick={() => insertAtCursor(`:${e.label}: `)}
                className="w-9 h-9 grid place-items-center rounded-sm hover:bg-cream">
                <img src={e.url} alt={e.label ?? ""} className="max-w-7 max-h-7" />
              </button>
            ))}
            <label className="w-9 h-9 grid place-items-center rounded-sm border border-dashed border-ink-12 hover:border-ink/40 cursor-pointer text-ink-40 text-[14px]">
              +
              <input type="file" accept="image/png,image/webp" hidden
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => e.target.files?.[0] && onEmojiUpload(e.target.files[0])} />
            </label>
          </div>
        </div>
      )}

      <textarea
        ref={taRef}
        value={text}
        onChange={(e) => onText(e.target.value)}
        rows={9}
        className="p-3 rounded-md border border-ink-12 bg-white text-[14px] leading-relaxed outline-none focus:border-ink resize-none"
      />
      <div className="text-right text-[11.5px] text-ink-40 font-mono">{text.length} / 4096</div>

      <div className="grid gap-1.5">
        <div className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">Медиа · фото или видео</div>
        <div className="flex gap-2 flex-wrap">
          {media.map((m) => (
            <div key={m.id}
              draggable
              onDragStart={(e) => onDragStart(e, m.id)}
              onDragOver={onDragOver}
              onDrop={(e) => onDrop(e, m.id)}
              className={cn(
                "relative w-20 h-20 rounded-md overflow-hidden bg-ink-6 group cursor-move",
                dragId === m.id && "opacity-50",
              )}
              title="Перетащите чтобы изменить порядок"
            >
              {m.kind === "video" ? (
                <div className="grid place-items-center w-full h-full text-ink-60 text-[12px]">▶ video</div>
              ) : (
                <img src={m.url} alt="" className="w-full h-full object-cover" />
              )}
              <button onClick={() => removeMedia(m.id)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-ink text-white grid place-items-center opacity-0 group-hover:opacity-100 text-[12px]">
                <Trash2 size={11} />
              </button>
              <div className="absolute top-1 left-1 text-ink-40 opacity-0 group-hover:opacity-80">
                <GripVertical size={12} />
              </div>
            </div>
          ))}
          <button onClick={() => fileRef.current?.click()} disabled={upload.isPending}
            className="w-20 h-20 grid place-items-center rounded-md border-2 border-dashed border-ink-12 text-[12px] font-display font-semibold text-ink-60 hover:border-ink hover:text-ink disabled:opacity-50">
            {upload.isPending ? "…" : "+ Загрузить"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Toolbar({ onBold, onItalic, onUnderline, onLink, onEmojiClick, emojiOpen }: {
  onBold: () => void; onItalic: () => void; onUnderline: () => void; onLink: () => void;
  onEmojiClick: () => void; emojiOpen: boolean;
}) {
  return (
    <div className="inline-flex gap-1 self-start p-1 rounded-md bg-ink-6">
      <TbBtn onClick={onBold}      title="Жирный (Cmd+B)"><Bold size={14} /></TbBtn>
      <TbBtn onClick={onItalic}    title="Курсив"><Italic size={14} /></TbBtn>
      <TbBtn onClick={onUnderline} title="Подчёркнутый"><Underline size={14} /></TbBtn>
      <span className="w-px self-stretch bg-ink-12 mx-0.5" />
      <TbBtn onClick={onLink}      title="Ссылка"><LinkIcon size={14} /></TbBtn>
      <TbBtn onClick={onEmojiClick} active={emojiOpen} title="Эмодзи"><Smile size={14} /></TbBtn>
    </div>
  );
}

function TbBtn({ onClick, children, title, active }: { onClick: () => void; children: React.ReactNode; title?: string; active?: boolean }) {
  return (
    <button onClick={onClick} title={title}
      className={cn("w-7 h-7 grid place-items-center rounded-sm transition-colors",
        active ? "bg-white text-ink shadow-sm" : "text-ink-60 hover:bg-white hover:text-ink")}>
      {children}
    </button>
  );
}

/**
 * Render the editor text as Telegram-style HTML, expanding **bold** / __italic__ /
 * ~underline~ / [text](url) / :emoji_name: markers to inline HTML.
 */
export function renderCreativeText(raw: string, emojiMap: Map<string, MediaItem>): string {
  const escape = (s: string) => s.replace(/[<>&"]/g, (ch) =>
    ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" } as Record<string, string>)[ch]!,
  );
  let html = escape(raw);
  html = html.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>");
  html = html.replace(/__(.+?)__/g, "<i>$1</i>");
  html = html.replace(/~(.+?)~/g, "<u>$1</u>");
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener" style="color:var(--wb-tg)">$1</a>');
  html = html.replace(/:([a-z0-9_-]+):/gi, (_, name) => {
    const e = emojiMap.get(name);
    if (e) return `<img src="${e.url}" alt=":${name}:" style="display:inline;height:18px;vertical-align:-3px"/>`;
    return `:${name}:`;
  });
  return html.replace(/\n/g, "<br/>");
}
